import React, { useEffect, useState } from 'react';
import { useLocales } from 'react-localized';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, Paper, TextField, Typography } from '@mui/material';
import { useNotification } from '../provider/NotificationProvider';
import { fetchPrices, updatePrices } from '../utils/api';

function PricesPage() {
  const { gettext } = useLocales();
  const { showNotification } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pricesJson, setPricesJson] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetch();
  }, []);

  const fetch = () => {
    setIsLoading(true);
    fetchPrices()
      .then(response => {
        setPricesJson(JSON.stringify(response, null, 2));
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        showNotification(gettext('Failed to fetch prices'), 'error');
      });
  };

  const handleSave = () => {
    try {
      const parsed = JSON.parse(pricesJson);
      setError('');
      setIsSaving(true);
      updatePrices(parsed)
        .then(() => {
          setIsSaving(false);
          showNotification(gettext('Prices updated successfully'), 'success');
        })
        .catch(err => {
          setIsSaving(false);
          showNotification(gettext('Failed to update prices'), 'error');
        });
    } catch (e) {
      setError(gettext('Invalid JSON format'));
    }
  };

  const handleJsonChange = (e) => {
    setPricesJson(e.target.value);
    if (error) {
      try {
        JSON.parse(e.target.value);
        setError('');
      } catch (err) {
        // keep error
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="warning">
          <AlertTitle>{gettext('Warning')}</AlertTitle>
          Updated prices will only apply to new requests; all existing requests will remain unchanged.
          <br />
          {gettext('Changing the JSON structure may break the price determination logic.')}
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          {gettext('Edit Prices')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={25}
            variant="outlined"
            value={pricesJson}
            onChange={handleJsonChange}
            error={!!error}
            helperText={error}
            sx={{ '& textarea': { fontFamily: 'monospace' } }}
          />
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={fetch}
              disabled={isSaving}
            >
              {gettext('Reset')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving || !!error}
            >
              {isSaving ? <CircularProgress size={24} /> : gettext('Save')}
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default PricesPage;
